import { loadSwiper } from './swiper-loader.js';

if (!customElements.get('featured-collections')) {
    class FeaturedCollections extends HTMLElement {
        constructor() {
            super();

            this.swipers = {};
            this.currentSwiper = null;
            this.focusIndex = 0;
        }

        connectedCallback() {
            this.init();
        }

        disconnectedCallback() {
            window.removeEventListener('resize', this.#onResize);
            this.#destroySwipers();
        }

        init() {
            this.#destroySwipers();

            this.elTabPanels = this.querySelectorAll('[data-featured-collections-tabpanel]');
            this.elTabs = this.querySelectorAll('[data-featured-collection-tab]');
            this.elNav = this.querySelectorAll('[data-featured-collections-nav]');
            this.elCarousels = this.querySelectorAll('[data-featured-collection-carousel]');

            if (!this.elTabPanels.length) return;

            this.elNav.forEach(el => el.addEventListener('click', this.navigateCarousel.bind(this)));
            window.addEventListener('resize', this.#onResize);

            this.#setupPanels();
            this.#bindTabs();
        }

        #onResize = () => {
            this.#updateNavState();
        };

        #destroySwipers() {
            for (const swiper of Object.values(this.swipers)) {
                swiper?.destroy?.(true, true);
            }

            this.swipers = {};
            this.currentSwiper = null;

            for (const el of this.querySelectorAll('[data-featured-collection-carousel]')) {
                el.removeAttribute('data-swiper-ready');
            }
        }

        #setupPanels() {
            this.elTabPanels.forEach((el, index) => {
                el.toggleAttribute('hidden', index !== 0);
                el.tabIndex = index === 0 ? 0 : -1;
            });
        }

        async #bindTabs() {
            this.elTabs.forEach(el => {
                el.addEventListener('click', this.setActiveTab.bind(this));
                el.addEventListener('keydown', this.#handleKeydown.bind(this));
            });

            await this.#initCarousels();

            if (this.elTabs[this.focusIndex]) {
                this.#activateTab(this.elTabs[this.focusIndex]);
            }
        }

        async #initCarousels() {
            const prevSlideMessage = this.dataset.prevSlideMessage || 'Previous slide';
            const nextSlideMessage = this.dataset.nextSlideMessage || 'Next slide';
            const Swiper = await loadSwiper();

            this.elCarousels.forEach(el => {
                const swiper = new Swiper(el, {
                    loop: false,
                    watchOverflow: true,
                    slidesPerView: 'auto',
                    slidesPerGroup: 1,
                    spaceBetween: 8,
                    slidesOffsetBefore: 12,
                    slidesOffsetAfter: 12,
                    autoplay: false,
                    a11y: {
                        enabled: true,
                        prevSlideMessage,
                        nextSlideMessage,
                    },
                    breakpoints: {
                        768: {
                            slidesPerView: 4,
                            slidesPerGroup: 4,
                            spaceBetween: 12,
                            slidesOffsetBefore: 0,
                            slidesOffsetAfter: 0
                        }
                    },
                    on: {
                        init: (swiper) => this.#onSwiperUpdate(swiper),
                        slideChange: (swiper) => this.#onSwiperUpdate(swiper),
                        breakpoint: (swiper) => this.#onSwiperUpdate(swiper),
                    }
                });

                this.swipers[el.id] = swiper;
                el.setAttribute('data-swiper-ready', '');
            });
        }

        #handleKeydown(event) {
            const tabs = [...this.elTabs];
            const currentIndex = tabs.indexOf(event.currentTarget);

            const nextIndex = (() => {
                switch (event.key) {
                    case 'ArrowRight': return (currentIndex + 1) % tabs.length;
                    case 'ArrowLeft': return (currentIndex - 1 + tabs.length) % tabs.length;
                    case 'Home': return 0;
                    case 'End': return tabs.length - 1;
                    default: return -1;
                }
            })();

            if (nextIndex < 0) return;

            event.preventDefault();
            const nextTab = tabs[nextIndex];
            nextTab.focus({ preventScroll: true });
            this.#activateTab(nextTab);
        }

        #getSwiperForPanel(panel) {
            const carousel = panel.querySelector('[data-featured-collection-carousel]');
            return carousel ? this.swipers[carousel.id] : null;
        }

        navigateCarousel({ currentTarget }) {
            if (!this.currentSwiper) return;

            const isNext = currentTarget.getAttribute('data-featured-collections-nav') === 'next';
            isNext ? this.currentSwiper.slideNext() : this.currentSwiper.slidePrev();
        }

        #onSwiperUpdate(swiper) {
            if (swiper === this.currentSwiper) {
                this.#updateNavState();
            }
        }

        #updateNavState() {
            if (!this.currentSwiper) return;

            this.elNav.forEach(el => {
                const direction = el.getAttribute('data-featured-collections-nav');

                if (direction === 'prev') {
                    el.disabled = this.currentSwiper.isBeginning;
                } else if (direction === 'next') {
                    el.disabled = this.currentSwiper.isEnd;
                }
            });
        }

        setActiveTab({ currentTarget }) {
            this.#activateTab(currentTarget);
        }

        #activateTab(elTab) {
            const activePanel = elTab.getAttribute('aria-controls');

            this.classList.add('loading');

            this.elTabPanels.forEach(el => {
                const isActive = el.id == activePanel;

                el.toggleAttribute('hidden', !isActive);
                el.tabIndex = isActive ? 0 : -1;

                if (isActive) {
                    this.currentSwiper = this.#getSwiperForPanel(el);
                    this.currentSwiper?.update();
                    this.#updateNavState();
                }
            });

            this.elTabs.forEach((el, index) => {
                const isActive = el == elTab;
                el.classList.toggle('active', isActive);
                el.setAttribute('aria-selected', isActive ? 'true' : 'false');
                el.setAttribute('tabindex', isActive ? '0' : '-1');

                if (isActive) {
                    el.focus({ preventScroll: true });
                    this.focusIndex = index;
                }
            });

            setTimeout(() => this.classList.remove('loading'), 300);
        }
    }

    customElements.define('featured-collections', FeaturedCollections);
}
